import { useEffect, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';

import { isChartableTag, Site } from '../../../models';

import { TagTree } from '../site/TagTree';
import { ViewTree } from '../site/ViewTree';

import Drawer from 'src/components/ui/drawer';
import TagsTabs, { AVAILABLE_TAGS } from './TagsTabs';

import { primaryColor, lightBlack, borderColor, grey } from 'src/data/colors';
import { SecondaryTextButton, PrimaryTextButton } from 'src/components/ui/Buttons';
import { Text, Heading5 } from 'src/components/ui/typography';
import CloseIcon from '@material-ui/icons/Close';
import { View } from 'src/state/contexts/analysisPage';
import { TagsDrawerStatus } from './AnalysisPage';
import { v4 } from 'uuid';

const useStyles = makeStyles((theme: Theme) => ({
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '14px',
    height: 'fit-content',
    padding: '16px',
  },
  tagToggle: {
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
  },
  viewsToggle: {
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
  },
  toggleSelected: {
    backgroundColor: primaryColor,
  },
  drawer: {
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.enteringScreen,
      easing: theme.transitions.easing.sharp,
    }),
    backgroundColor: '#f8f8f8',
    borderLeft: '1px solid #e0e0e0',
    bottom: 0,
    flexDirection: 'column',
    right: 0,
    top: 0,
    outline: '1px solid red',
  },
  dateRangeContent: {
    backgroundColor: 'white',
    border: `1px solid ${lightBlack}`,
    borderRadius: 8,
    height: 'fit-content',
    padding: 16,
  },
  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '480px',
  },
  contentContainer: {
    flex: 1,
    overflow: 'hidden',
    padding: '0 16px',
  },
  footer: {
    padding: '16px',
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px',
  },
  drawerFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px',
    borderTop: `1px solid ${borderColor}`,
  },
  closeIcon: {
    color: grey,
  },
}));

const MAX_TAGS = 8;

interface TagsSelectorProps {
  maxUnits: number;
  site: Site;
  units: string[];
  isDrawerOpen: TagsDrawerStatus;
  setIsDrawerOpen: (tagsDrawerStatus: TagsDrawerStatus) => void;
  updateTagsForChartInView: (viewId: string, chartId: string, tagIds: string[]) => void;
  updateViews: (views: View[]) => void;
  views: View[];
}

const TagsSelector = ({
  maxUnits,
  site,
  units,
  isDrawerOpen,
  setIsDrawerOpen,
  updateViews,
  views,
  updateTagsForChartInView,
}: TagsSelectorProps) => {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState<
    typeof AVAILABLE_TAGS.tags | typeof AVAILABLE_TAGS.views
  >(AVAILABLE_TAGS.views);
  const [selectedTags, setSelectedTags] = useState<string[]>(
    views
      .find((view) => view.id === isDrawerOpen.viewId)
      ?.charts.find((chart) => chart.id === isDrawerOpen.chartId)?.tagsIds || []
  );
  const [selectedViews, setSelectedViews] = useState<View[]>([]);

  useEffect(() => {
    // prevent re-render update when closing the drawer
    if (isDrawerOpen.isOpen) {
      if (selectedTab === AVAILABLE_TAGS.views) {
        setSelectedViews(views);
      } else {
        setSelectedTags(
          views
            .find((view) => view.id === isDrawerOpen.viewId)
            ?.charts.find((chart) => chart.id === isDrawerOpen.chartId)?.tagsIds || []
        );
      }
    }
  }, [isDrawerOpen.viewId, isDrawerOpen.chartId, views, isDrawerOpen.isOpen, selectedTab]);

  const handleApply = () => {
    if (selectedTab === AVAILABLE_TAGS.views) {
      updateViews(selectedViews);
    } else {
      if (isDrawerOpen.viewId && isDrawerOpen.chartId) {
        updateTagsForChartInView(isDrawerOpen.viewId, isDrawerOpen.chartId, selectedTags);
      } else {
        updateViews([
          {
            id: v4(),
            label: null,
            charts: [
              {
                id: v4(),
                tagsIds: selectedTags,
                label: null,
              },
            ],
          },
        ]);
      }
    }
    setIsDrawerOpen({ isOpen: false, viewId: null, chartId: null });
  };

  const handleToggleTag = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter((id) => id !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const handleClearAllTAgs = () => {
    setSelectedTags([]);
  };

  const handleClose = () => {
    setSelectedTags([]);
    setIsDrawerOpen({ isOpen: false, viewId: null, chartId: null });
  };

  const renderContent = () => {
    if (selectedTab === AVAILABLE_TAGS.tags) {
      return (
        <>
          <TagTree
            hideSelectedWhenEmpty={false}
            clearAll={handleClearAllTAgs}
            onTagSelect={handleToggleTag}
            selectedTags={selectedTags}
            site={site}
            tagFilter={isChartableTag}
            disabled={(tag) =>
              (!units.includes(tag.unit) && units.length >= maxUnits) ||
              selectedTags.length >= MAX_TAGS
            }
            tabs={
              <TagsTabs
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                showViews={true}
              />
            }
          />
        </>
      );
    }

    return (
      <>
        <ViewTree
          onTagSelect={handleToggleTag}
          selectedTags={selectedTags}
          site={site}
          tagFilter={isChartableTag}
          disabled={(tag) =>
            (!units.includes(tag.unit) && units.length >= maxUnits) ||
            selectedTags.length >= MAX_TAGS
          }
          selectedViews={selectedViews}
          setSelectedViews={setSelectedViews}
          tabs={
            <TagsTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} showViews={true} />
          }
        />
      </>
    );
  };

  return (
    <Drawer open={isDrawerOpen.isOpen} onClose={handleClose} anchor="right">
      <div className={classes.drawerHeader}>
        <Heading5>Select Points</Heading5>
        <CloseIcon onClick={handleClose} className={classes.closeIcon} />
      </div>
      <div className={classes.drawerContent}>
        <div className={classes.contentContainer}>{renderContent()}</div>

        <Text fontSize={12} lineHeight="18px" className={classes.footer}>
          * A maximum of {maxUnits} different units of measure may be visualized, or {MAX_TAGS} tags
          total.
        </Text>
      </div>
      <div className={classes.drawerFooter}>
        <SecondaryTextButton onClick={handleClose}>Cancel</SecondaryTextButton>
        <PrimaryTextButton onClick={handleApply}>Apply</PrimaryTextButton>
      </div>
    </Drawer>
  );
};

export default TagsSelector;
